// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Img from "@store/MobiusOverrides/Img/Img";
import { StyledClickable } from "@store/MobiusOverrides/Link/Link";
import Typography, { TypographyPresentationProps } from "@store/MobiusOverrides/Typography/Typography";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { FC, useState } from "react";
import { css } from "styled-components";

const enum fields {
    autoplayVideos = "autoplayVideos",
    links = "links",
}

export interface InstructionVideoModel {
    fields: {
        videoImage: string;
        videoTitle: string;
        videoContent: string;
        videoLink: string;
    };
}

export interface InstructionVideosProps extends WidgetProps {
    fields: {
        [fields.autoplayVideos]: boolean;
        [fields.links]: InstructionVideoModel[];
    };
}

export interface InstructionVideosStyles {
    buttonStyle?: ButtonPresentationProps;
    videoLinkWrapper?: InjectableCss;
    videoLinkItem?: InjectableCss;
    videoLinkItemImage?: InjectableCss;
    videoTitle?: TypographyPresentationProps;
    videoText?: InjectableCss;
    iFrameWrapper?: InjectableCss;
}

export const InstructionVideosStyles: InstructionVideosStyles = {
    buttonStyle: {
        sizeVariant: "large",
        css: css`
            margin-top: ${pxToRem(10)};
        `,
    },
    videoLinkWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            margin-top: ${pxToRem(30)};
        `,
    },
    videoLinkItem: {
        css: css`
            display: flex;
            flex-direction: row;
            width: 50%;
            padding: ${pxToRem(20)};
            ${breakpointValues(["100%", null, "50%"], "width")};
            transition: 0.3s ease-out;

            &:hover {
                cursor: pointer;
                color: ${getColor("text.linkAltHover")};
            }

            &.activeVideo {
                border: 2px solid ${getColor("success.main")};
                border-radius: ${pxToRem(20)};
                box-shadow: ${x => x.theme.shadows[2]};
                color: ${getColor("text.linkAltHover")};
            }
        `,
    },
    videoLinkItemImage: {
        css: css`
            width: ${pxToRem(115)};
            height: ${pxToRem(115)};
        `,
    },
    videoTitle: {
        weight: "bold",
        variant: "h4",
    },
    videoText: {
        css: css`
            padding-left: ${pxToRem(15)};
        `,
    },
    iFrameWrapper: {
        css: css`
            position: relative;
            width: 100%;
            height: 0;
            padding-top: 56.25%;
            overflow: hidden;

            iframe {
                position: absolute;
                top: 0;
                width: 100%;
                height: 100%;
            }
        `,
    },
};

const styles = InstructionVideosStyles;

const InstructionVideos: FC<InstructionVideosProps> = ({ fields }) => {
    const { isInShell } = useShellContext();

    if (!fields.links?.length && !isInShell) {
        return null;
    }

    const [videoUrl, setVideoUrl] = useState(fields.links[0]?.fields?.videoLink || "");

    const srcOptions = fields.autoplayVideos
        ? "?rel=0&modestbranding=1&autohide=1&mute=1&showinfo=0&controls=1&autoplay=1"
        : "?rel=0&modestbranding=1&autohide=1&showinfo=0&controls=1";

    const videoLinkOnClick = (videoLink: string) => {
        setVideoUrl(videoLink);
        window.scrollTo(0, 0);
    };
    return (
        <StyledWrapper>
            {fields.links?.length ? (
                <>
                    <StyledWrapper {...styles.iFrameWrapper}>
                        <iframe
                            src={`${videoUrl}${srcOptions}`}
                            title="YouTube video player"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            referrerPolicy="strict-origin-when-cross-origin"
                            allowFullScreen
                        ></iframe>
                    </StyledWrapper>
                    <StyledWrapper>
                        <StyledWrapper as="ul" {...styles.videoLinkWrapper}>
                            {fields.links.map((link, index) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <StyledClickable
                                    as="li"
                                    className={`links-wrapper ${videoUrl === link.fields.videoLink && "activeVideo"}`}
                                    key={index}
                                    {...styles.videoLinkItem}
                                    onClick={() => videoLinkOnClick(link.fields.videoLink)}
                                >
                                    <Img
                                        className="img"
                                        src={link.fields.videoImage}
                                        altText={link.fields.videoTitle}
                                        loading="lazy"
                                        {...styles.videoLinkItemImage}
                                    />
                                    <StyledWrapper {...styles.videoText}>
                                        <Typography {...styles.videoTitle}>{link.fields.videoTitle}</Typography>
                                        <Typography as="p">{link.fields.videoContent}</Typography>
                                    </StyledWrapper>
                                </StyledClickable>
                            ))}
                        </StyledWrapper>
                    </StyledWrapper>
                </>
            ) : (
                <Typography>{translate("Please add videos")}</Typography>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: InstructionVideos,
    definition: {
        displayName: "Instruction Videos",
        group: "Sign In",
        allowedContexts: ["InstructionVideosPage"],
        fieldDefinitions: [
            {
                name: fields.autoplayVideos,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.videoTitle;
                },
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "videoImage",
                        defaultValue: "",
                        editorTemplate: "ImagePickerField",
                        isRequired: true,
                    },
                    {
                        name: "videoTitle",
                        displayName: "Video Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "videoContent",
                        displayName: "Video Content",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "videoLink",
                        displayName: "Video Link",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
